import React, { FC, PropsWithChildren } from 'react';
import Modal from 'react-native-modal';
import { StyleProp, TouchableWithoutFeedback, View, ViewStyle } from 'react-native';
import { ColorFunction } from '@styles/colors/types';
import { fullHeight, fullWidth, scale as initialScale } from '@components/frame';
import { ScaleFunction } from '@components/operatorPanel/interfaces';

export interface ModalWrapperProps extends PropsWithChildren {
  onBackdropPress: () => void;
  C: ColorFunction;
  backdropColor?: string;
  backdropOpacity?: number;
  isVisible?: boolean;
  animationIn?: 'fadeIn' | 'slideInUp' | 'slideInDown' | 'slideInLeft' | 'slideInRight';
  animationOut?: 'fadeOut' | 'slideOutDown' | 'slideOutUp' | 'slideOutLeft' | 'slideOutRight';
  animationInTiming?: number;
  animationOutTiming?: number;
  withoutAnimation?: boolean;
  withPositionedContainer?: boolean;
  positionedContainerStyles?: StyleProp<ViewStyle>;
  scale?: ScaleFunction;
}

interface ModalContentProps
  extends PropsWithChildren,
    Pick<ModalWrapperProps, 'C' | 'withPositionedContainer' | 'positionedContainerStyles' | 'scale'> {}

export const ModalContent: FC<ModalContentProps> = ({ withPositionedContainer, positionedContainerStyles, C, scale = initialScale, children }) => {
  if (!withPositionedContainer) {
    return children;
  }

  return (
    <View
      style={[
        {
          position: 'absolute',
          backgroundColor: C('Background/Light/Field'),
          borderRadius: scale(12),
        },
        positionedContainerStyles,
      ]}
    >
      {children}
    </View>
  );
};

const ModalWrapper: FC<ModalWrapperProps> = ({
  C,
  onBackdropPress,
  children,
  scale,
  withPositionedContainer = false,
  positionedContainerStyles,
  backdropColor = 'Overlay/Light/Dim light',
  withoutAnimation = false,
  isVisible = true,
  backdropOpacity = 1,
  animationIn = 'fadeIn',
  animationOut = 'fadeOut',
  animationInTiming = 300,
  animationOutTiming = 300,
}) => {
  return (
    <Modal
      isVisible={isVisible}
      customBackdrop={
        <TouchableWithoutFeedback onPress={onBackdropPress}>
          <View
            style={{
              width: fullWidth,
              height: fullHeight,
              opacity: backdropOpacity,
              backgroundColor: backdropColor === 'transparent' ? 'transparent' : C(backdropColor),
            }}
          />
        </TouchableWithoutFeedback>
      }
      animationIn={withoutAnimation ? 'fadeIn' : animationIn}
      animationOut={withoutAnimation ? 'fadeOut' : animationOut}
      animationInTiming={withoutAnimation ? 1 : animationInTiming}
      animationOutTiming={withoutAnimation ? 1 : animationOutTiming}
      backdropTransitionInTiming={withoutAnimation ? 1 : animationInTiming}
      backdropTransitionOutTiming={withoutAnimation ? 1 : animationOutTiming}
      style={{ margin: 0 }}
    >
      <ModalContent withPositionedContainer={withPositionedContainer} positionedContainerStyles={positionedContainerStyles} C={C} scale={scale}>
        {children}
      </ModalContent>
    </Modal>
  );
};

export default ModalWrapper;
